/**
 * Campaign detail: "Who writes the messages" (2026-10-03).
 *
 * settings.message_writer = 'dca' (default, DealCloser AI) | 'mcp' (the
 * user's own Claude through the MCP connector). On 'mcp' DealCloser still
 * sends the connection requests but leaves the DMs, follow-ups and replies to
 * Claude; settings.mcp_fallback_hours (0 = never) hands a due message back to
 * DealCloser AI when Claude has not written it in time. Saved through the
 * existing PUT /api/campaigns/:id, which merges `settings` keys.
 *
 * The card titles and descriptions are shared with the create wizard's
 * Method step (MESSAGE_WRITER_COPY). On Your Claude it also says what
 * happens to messages already waiting for review and when DealCloser AI
 * takes over (Eli's 10/21 question, plan N8).
 */

import { Link } from 'react-router-dom'
import { Bot, Check, Loader2, Sparkles, AlertTriangle, FileText } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FieldHint } from '@/components/field-hint'
import { cn } from '@/lib/utils'
import {
  CLAUDE_SETTINGS_PATH,
  FALLBACK_OPTIONS,
  MESSAGE_WRITER_COPY,
  MESSAGE_WRITER_ORDER,
  hasTemplateText,
  hasWriteConnection,
  messageWriterPatch,
  readFallbackHours,
  readMessageWriter,
  type MessageWriter,
} from '@/features/settings/mcp-helpers'
import { useMcpConnections } from '@/features/settings/use-mcp'
import { useApprovals } from '@/features/inbox/use-inbox'
import { useUpdateCampaign } from './use-campaigns'
import type { Campaign } from './types'

const ICONS: Record<MessageWriter, typeof Bot> = { dca: Sparkles, mcp: Bot, template: FileText }

const OPTIONS: ReadonlyArray<{ value: MessageWriter; title: string; body: string; Icon: typeof Bot }> =
  MESSAGE_WRITER_ORDER.map((value) => ({
    value,
    title: MESSAGE_WRITER_COPY[value].label,
    body: MESSAGE_WRITER_COPY[value].hint,
    Icon: ICONS[value],
  }))

/**
 * What switching to Your Claude does to messages already in flight (plan
 * N8). Review queue rows carry the campaign's name, not its id, so the
 * count is matched by name.
 */
function McpSwitchNote({ campaign, fallbackHours }: { readonly campaign: Campaign; readonly fallbackHours: number }) {
  const { data: approvals } = useApprovals()
  const waiting = approvals?.filter((a) => a.campaign_name === campaign.name).length
  const queued =
    waiting === undefined
      ? 'Messages DealCloser AI already wrote that wait in Inbox, Review stay there for you to send or discard.'
      : waiting === 0
        ? 'No messages from this campaign are waiting in Inbox, Review.'
        : waiting === 1
          ? '1 message from this campaign is waiting in Inbox, Review. It stays there for you to send or discard.'
          : `${waiting} messages from this campaign are waiting in Inbox, Review. They stay there for you to send or discard.`
  return (
    <div className="flex flex-col gap-1 rounded-md border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
      <p className="font-medium text-foreground">Messages already in progress</p>
      <p>
        Messages already sent stay as they are. {queued} From now on your Claude writes every due DM, follow-up
        and reply. DealCloser still sends the connection requests.
      </p>
      <p>
        {fallbackHours === 0
          ? 'Hand back is Off, so DealCloser AI never writes for this campaign. Run your Claude routine regularly, or set a hand back below.'
          : `If your Claude has not written a due message after ${fallbackHours} hours, DealCloser AI writes it, so a lead is not left waiting when Claude does not run.`}
      </p>
    </div>
  )
}

export function MessageWriterCard({ campaign }: { readonly campaign: Campaign }) {
  const writer = readMessageWriter(campaign)
  // "Your templates" only makes sense when the steps carry template text
  // (set in the wizard's template mode, card S3).
  const options = OPTIONS.filter((o) => o.value !== 'template' || writer === 'template' || hasTemplateText(campaign))
  const fallbackHours = readFallbackHours(campaign)
  const update = useUpdateCampaign()
  const { data: mcp } = useMcpConnections()
  const hasWriter = mcp ? hasWriteConnection(mcp.connections) : true
  const disabled = update.isPending || campaign.status === 'deleted'

  function save(next: MessageWriter, hours: number) {
    update.mutate({ id: campaign.id, updates: { settings: messageWriterPatch(next, hours) } })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-sm font-semibold">
          Who writes the messages
          <FieldHint label="Who writes the messages?">
            Choose who writes the LinkedIn messages for this campaign. Either way DealCloser decides who is due
            and when, quality checks every message, and keeps your daily limits and sending hours.
          </FieldHint>
          {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div
          className={cn('grid gap-2', options.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}
          role="radiogroup"
          aria-label="Who writes the messages"
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={writer === o.value}
              disabled={disabled}
              onClick={() => writer !== o.value && save(o.value, fallbackHours)}
              className={cn(
                'flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors disabled:opacity-60',
                writer === o.value ? 'border-cobalt-bright bg-primary/10 ring-1 ring-cobalt-bright' : 'hover:bg-muted/50',
              )}
            >
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <o.Icon className="h-4 w-4" />
                {o.title}
                {writer === o.value && <Check aria-hidden className="ml-auto h-4 w-4 text-cobalt-bright" />}
              </span>
              <span className="text-xs text-muted-foreground">{o.body}</span>
            </button>
          ))}
        </div>

        {writer === 'mcp' && (
          <>
            <McpSwitchNote campaign={campaign} fallbackHours={fallbackHours} />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`fallback-${campaign.id}`} className="flex items-center gap-1.5 text-xs">
                Hand back to DealCloser AI
                <FieldHint label="What is the hand back?">
                  If Claude has not written a due message after this long, DealCloser AI writes it so leads are
                  not left waiting. Off means DealCloser never steps in for this campaign; you still get a daily
                  notification if something has waited more than 24 hours.
                </FieldHint>
              </Label>
              <Select
                value={String(fallbackHours)}
                onValueChange={(v) => save('mcp', Number(v))}
                disabled={disabled}
              >
                <SelectTrigger id={`fallback-${campaign.id}`} className="sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FALLBACK_OPTIONS.map((o) => (
                    <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!hasWriter && (
              <p className="flex items-start gap-1.5 text-xs text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  You have no Claude connection that can send messages yet.{' '}
                  <Link to={CLAUDE_SETTINGS_PATH} className="underline">Set one up in Settings</Link>.
                </span>
              </p>
            )}
            {mcp && !mcp.writes_enabled && (
              <p className="flex items-start gap-1.5 text-xs text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Sending through Claude is paused by DealCloser for now: Claude&rsquo;s messages wait in your review
                  queue (Inbox) for you to send, or the hand back above lets DealCloser&rsquo;s AI write them.
                </span>
              </p>
            )}
            <Link to={`${CLAUDE_SETTINGS_PATH}#claude-guide`} className="text-xs text-cobalt-bright underline">
              How to let Claude run your messaging
            </Link>
          </>
        )}
        {update.error instanceof Error && <p className="text-xs text-destructive">{update.error.message}</p>}
      </CardContent>
    </Card>
  )
}
